import { c as _c } from "react/compiler-runtime";
import { MARKETING_TACTICS, getCustomerFunnelStageLabel, getEntityCapabilityForTactic } from '@sizzlenz/shared';
import Link from 'next/link';
import { useState } from 'react';
import { ChevronDown, ChevronUp, Lightbulb, Store, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buildDirectoryHref } from '@/lib/directory-filters';

/** Number of tactic cards shown before the grid is expanded. */
const INITIAL_VISIBLE_TACTICS = 6;
const TACTIC_CARD_TONES = ['bg-[#fff1e9]', 'bg-[#fff8d9]', 'bg-[#eeeafd]', 'bg-[#e9f9f3]'] as const;
const TEXT = {
  // Tactic card title
  tacticTitle: 'text-base font-black',
  // Tactic card body
  tacticBody: 'mt-1.5 text-sm leading-relaxed',
  // Footnote / helper text
  footnote: 'text-sm leading-6'
};

// Link each tactic card to the inspiration feed pre-filtered by that tactic.
// The `tactics` param + id must match the URL contract in InspirationFeed.
const buildTacticFeedHref = (tacticId: string) => `/inspiration-feed?tactics=${encodeURIComponent(tacticId)}`;
const journeyTactics = MARKETING_TACTICS.map((tactic, index) => {
  const capability = getEntityCapabilityForTactic(tactic.id);
  return {
    id: tactic.id,
    title: tactic.label,
    copy: tactic.description,
    tone: TACTIC_CARD_TONES[index % TACTIC_CARD_TONES.length],
    phases: tactic.journeyStages.map(getCustomerFunnelStageLabel),
    examplesHref: buildTacticFeedHref(tactic.id),
    // Providers link only exists when the tactic maps to a directory capability.
    providersHref: capability ? buildDirectoryHref('capability', capability) : undefined
  };
});
interface DemandTacticsBlockProps {
  /**
   * Controls whether the consumer journey stage badges are rendered on each
   * tactic card. Defaults to true.
   */
  showStageBadges?: boolean;
  className?: string;
}

/**
 * "Tactics you can use" framework block for the Demand Central page.
 * Renders shopper-marketing tactics, optionally with consumer stage badges.
 */
export function DemandTacticsBlock(t0) {
  const $ = _c(31);
  const {
    showStageBadges: t1,
    className
  } = t0;
  const showStageBadges = t1 === undefined ? true : t1;
  const [expanded, setExpanded] = useState(false);
  const hasMore = journeyTactics.length > INITIAL_VISIBLE_TACTICS;
  let hiddenCount;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[0] !== className || $[1] !== expanded || $[2] !== showStageBadges) {
    const visibleTactics = expanded ? journeyTactics : journeyTactics.slice(0, INITIAL_VISIBLE_TACTICS);
    hiddenCount = journeyTactics.length - INITIAL_VISIBLE_TACTICS;
    if ($[10] !== className) {
      t6 = cn("overflow-hidden border-2 border-gray-950 bg-white", className);
      $[10] = className;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex items-start gap-3 border-b-2 border-gray-950 bg-[#f3eee7] px-5 py-5"><Store className="h-5 w-5 shrink-0 text-[#d63f20]" aria-hidden="true" /><div><p className="text-2xl font-black tracking-[-0.03em] sm:text-3xl">Tactics to be seen and be chosen</p></div></div>;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t4 = "p-5 sm:p-6";
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <p className="mb-5 max-w-3xl text-base leading-7 text-gray-600">Tactics are the moves you make to create and convert demand. Each one is a way to reach people, give them a reason to care, and make it easier to try or buy. There's no single right answer. The strongest plans combine a few tactics that fit your product, your moment and what's stopping people from choosing you. Use these as a starting point, then explore real examples and the partners who can help you run them.</p>;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t2 = "grid gap-3 sm:grid-cols-2";
    let t8;
    if ($[14] !== showStageBadges) {
      t8 = tactic => <div key={tactic.title} className={`group flex flex-col border border-gray-300 px-5 py-4 transition-transform hover:-translate-y-0.5 hover:border-gray-950 ${tactic.tone}`}><p className={`${TEXT.tacticTitle}`}>{tactic.title}</p><p className={`${TEXT.tacticBody} text-gray-600`}>{tactic.copy}</p>{showStageBadges && <div className="mt-3 flex flex-wrap gap-1.5">{tactic.phases.map(_temp)}</div>}<div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-gray-950/10 pt-3"><Link href={tactic.examplesHref} className="inline-flex cursor-pointer items-center gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#d63f20] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]"><Lightbulb className="h-3.5 w-3.5" aria-hidden="true" />Inspiration</Link>{tactic.providersHref && <Link href={tactic.providersHref} className="inline-flex cursor-pointer items-center gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#211d4f] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]"><Users className="h-3.5 w-3.5" aria-hidden="true" />Who can help</Link>}</div></div>;
      $[14] = showStageBadges;
      $[15] = t8;
    } else {
      t8 = $[15];
    }
    t3 = visibleTactics.map(t8);
    $[0] = className;
    $[1] = expanded;
    $[2] = showStageBadges;
    $[3] = hiddenCount;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
  } else {
    hiddenCount = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
  }
  let t8;
  if ($[16] !== t2 || $[17] !== t3) {
    t8 = <div className={t2}>{t3}</div>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== expanded || $[20] !== hiddenCount) {
    t9 = hasMore && <div className="mt-4 flex justify-center"><button type="button" onClick={() => setExpanded(_temp2)} aria-expanded={expanded} className="inline-flex cursor-pointer items-center gap-1.5 border-2 border-gray-950 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-gray-950 outline-none transition-colors hover:bg-gray-950 hover:text-white focus-visible:ring-2 focus-visible:ring-[#211d4f]">{expanded ? <>Show fewer tactics<ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /></> : <>Show {hiddenCount} more {hiddenCount === 1 ? "tactic" : "tactics"}<ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /></>}</button></div>;
    $[19] = expanded;
    $[20] = hiddenCount;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t4 || $[23] !== t5 || $[24] !== t8 || $[25] !== t9) {
    t10 = <div className={t4}>{t5}{t8}{t9}</div>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t6 || $[29] !== t7) {
    t11 = <div className={t6}>{t7}{t10}</div>;
    $[27] = t10;
    $[28] = t6;
    $[29] = t7;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  return t11;
}
function _temp2(prev) {
  return !prev;
}
function _temp(phase) {
  return <span key={phase} className="rounded-full border border-gray-950/15 bg-white/70 px-2.5 py-0.5 text-[10px] font-black uppercase tracking-[0.08em] text-gray-700">{phase}</span>;
}